import React from 'react';
import './SetupInstructions.css';

const SetupInstructions = () => {
  return (
    <div className="setup-container">
      <h1>MIDI Setup</h1>
      
      <section className="setup-section">
        <h2>🎹 macOS MIDI Setup</h2>
        <div className="setup-content">
          <h3>Configure IAC Driver (Inter-Application Communication)</h3>
          <ol>
            <li>Open <strong>Audio MIDI Setup</strong>
              <ul>
                <li>Navigate to: Applications → Utilities → Audio MIDI Setup</li>
                <li>Or use Spotlight (⌘ + Space) and search "Audio MIDI Setup"</li>
              </ul>
            </li>
            <li>Open MIDI Studio
              <ul>
                <li>Go to <strong>Window → Show MIDI Studio</strong></li>
                <li>Or press <kbd>⌘ + 2</kbd></li>
              </ul>
            </li>
            <li>Configure IAC Driver
              <ul>
                <li>Double-click on <strong>"IAC Driver"</strong> in the MIDI Studio window</li>
                <li>Check the box: <strong>"Device is online"</strong></li>
              </ul>
            </li>
            <li>Add MIDI Ports (if needed)
              <ul>
                <li>In the IAC Driver Properties window, you'll see a list of ports</li>
                <li>Default port is usually "IAC Driver Bus 1"</li>
                <li>Click the <strong>"+"</strong> button to add more ports</li>
                <li>Rename ports as needed (e.g., "Browser MIDI 1", "Browser MIDI 2")</li>
                <li>For this application, at least <strong>1 port</strong> is required</li>
              </ul>
            </li>
            <li>Apply Changes
              <ul>
                <li>Click <strong>"Apply"</strong> to save your settings</li>
                <li>Close the IAC Driver Properties window</li>
              </ul>
            </li>
            <li>Restart Your Browser
              <ul>
                <li>Close all browser windows completely</li>
                <li>Reopen the browser to detect the new MIDI ports</li>
              </ul>
            </li>
          </ol>
          
          <div className="info-box">
            <strong>💡 Tip:</strong> You can verify the ports are working by checking the browser console for "Available MIDI output" messages when you load this application.
          </div>
        </div>
      </section>

      <section className="setup-section">
        <h2>🎛️ DAW Setup (Ableton Live)</h2>
        <div className="setup-content">
          <h3>Configure Ableton Live to Receive MIDI</h3>
          <ol>
            <li>Open Ableton Live Preferences
              <ul>
                <li>macOS: <strong>Ableton Live → Preferences</strong></li>
                <li>Or press <kbd>⌘ + ,</kbd></li>
              </ul>
            </li>
            <li>Navigate to Link/Tempo/MIDI
              <ul>
                <li>Click on the <strong>"Link/Tempo/MIDI"</strong> tab</li>
              </ul>
            </li>
            <li>Enable MIDI Ports
              <ul>
                <li>In the <strong>"MIDI Ports"</strong> section, find your IAC Driver ports</li>
                <li>Under the <strong>"Input"</strong> column, enable the ports you configured</li>
                <li>Set both <strong>"Track"</strong> and <strong>"Remote"</strong> to <strong>On</strong></li>
              </ul>
            </li>
            <li>Create MIDI Tracks
              <ul>
                <li>Create a new MIDI track: <kbd>⌘ + Shift + T</kbd></li>
                <li>In the track's input settings (below track name), select:
                  <ul>
                    <li><strong>MIDI From:</strong> Your IAC Driver port (e.g., "IAC Driver Bus 1")</li>
                    <li><strong>Channel:</strong> All or specific channel (1-16)</li>
                  </ul>
                </li>
              </ul>
            </li>
            <li>Arm the Track
              <ul>
                <li>Click the <strong>Arm</strong> button on the MIDI track (red circle icon)</li>
                <li>Or enable <strong>Monitor</strong> to hear MIDI without recording</li>
              </ul>
            </li>
            <li>Load an Instrument
              <ul>
                <li>Drag an instrument (e.g., Wavetable, Operator, Simpler) onto the MIDI track</li>
                <li>Or use any third-party VST/AU instrument</li>
              </ul>
            </li>
          </ol>

          <div className="info-box">
            <strong>💡 Tip:</strong> You can monitor multiple MIDI channels by setting different tracks to receive from the same IAC port but on different channels (1-16).
          </div>
        </div>
      </section>

      <section className="setup-section">
        <h2>🎹 Other DAWs</h2>
        <div className="setup-content">
          <h3>Logic Pro / GarageBand</h3>
          <ol>
            <li>Create a new Software Instrument track</li>
            <li>In the track settings, set the <strong>Input</strong> to the IAC Driver port</li>
            <li>The track will automatically receive MIDI from the browser</li>
          </ol>

          <h3>Other DAWs</h3>
          <p>
            Most DAWs will automatically detect virtual MIDI ports. Look for the IAC Driver 
            ports in your DAW's MIDI input settings and enable them. The exact steps vary by 
            DAW but generally involve:
          </p>
          <ul>
            <li>Opening MIDI preferences/settings</li>
            <li>Enabling the IAC Driver port as an input</li>
            <li>Creating a MIDI track that receives from that port</li>
            <li>Loading an instrument on the track</li>
          </ul>
        </div>
      </section>

      <section className="setup-section">
        <h2>🐛 Troubleshooting</h2>
        <div className="setup-content">
          <h3>No MIDI Ports Detected in Browser</h3>
          <ul>
            <li>Ensure IAC Driver is online in Audio MIDI Setup</li>
            <li>Restart your browser completely (close all windows)</li>
            <li>Check browser console for Web MIDI API errors</li>
            <li>Try a different browser (Chrome, Edge, or Opera)</li>
          </ul>

          <h3>DAW Not Receiving MIDI</h3>
          <ul>
            <li>Verify the IAC Driver port is enabled in your DAW's MIDI settings</li>
            <li>Check that the MIDI track is armed and monitoring input</li>
            <li>Ensure the correct MIDI port is selected in both the browser and DAW</li>
            <li>Try sending a test note from the browser to verify the connection</li>
          </ul>

          <h3>Audio Latency / Timing Issues</h3>
          <ul>
            <li>Reduce your DAW's audio buffer size for lower latency</li>
            <li>Close unnecessary applications to free up system resources</li>
            <li>Use a wired internet connection if fetching data from remote APIs</li>
          </ul>

          <h3>Performance Issues</h3>
          <ul>
            <li>Close unnecessary browser tabs</li>
            <li>Disable browser extensions that might interfere with timing</li>
            <li>Use a dedicated browser instance for MIDI work</li>
          </ul>
        </div>
      </section>

      <section className="setup-section">
        <h2>✅ Quick Checklist</h2>
        <div className="setup-content">
          <div className="checklist">
            <label>
              <input type="checkbox" /> IAC Driver is online in Audio MIDI Setup
            </label>
            <label>
              <input type="checkbox" /> At least one IAC port is configured
            </label>
            <label>
              <input type="checkbox" /> Browser has been restarted after MIDI setup
            </label>
            <label>
              <input type="checkbox" /> DAW MIDI preferences show IAC ports enabled
            </label>
            <label>
              <input type="checkbox" /> MIDI track is created and armed in DAW
            </label>
            <label>
              <input type="checkbox" /> Instrument is loaded on the MIDI track
            </label>
            <label>
              <input type="checkbox" /> Browser console shows MIDI ports detected
            </label>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SetupInstructions;

